// Server-safe landing catalog (no "use client", type-only imports). Lives apart
// from LandingToolsGrid so server files (app/layout.tsx, app/(public)/page.tsx
// meta descriptions, the tool-release guard test) can read the tool count and
// category membership without pulling a client component into the server graph.
import type { ServiceKey } from "@/hooks/useSiteSettings";

type LandingToolKey = ServiceKey;

export type LandingCategoryId = "vehicles" | "props" | "clothing" | "weapons" | "scripts";
export type LandingFilter = LandingCategoryId | "all";
export type LandingCategory = { id: LandingCategoryId; label: string; hint: string; keys: LandingToolKey[] };

// Tools grouped by the asset they produce — the same segmentation the hub uses
// (Vehicles / Clothing / Props / Weapons / Scripts). Order within a group is
// re-sorted by sort_order at render time, so the key order here is incidental.
export const LANDING_CATEGORIES: LandingCategory[] = [
  { id: "vehicles", label: "Vehicles", hint: "Liveries, meta, lights, and textures.",     keys: ["paintboothstudio", "paintbooth", "uvstudio", "blinklab", "blinklab3d", "modkitviewer", "chevronbuilder", "creatorconvert", "metaroom", "handlingdiff"] },
  { id: "clothing", label: "Clothing", hint: "EUP outfits, patches, badges, and add-on packs.", keys: ["eupviewer", "addoneupcreator", "badgemaker", "embroiderytool", "inklab"] },
  { id: "props",    label: "Props",    hint: "Generate 3D models and item art.",          keys: ["tripoai", "itemimage"] },
  { id: "weapons",  label: "Weapons",  hint: "Weapon textures and 3D previews.",          keys: ["weaponviewer"] },
  { id: "scripts",  label: "Scripts",  hint: "Audit, package, and document resources.",   keys: ["codesmith", "codevisualizer", "scriptdocs", "packbench", "resmon"] },
];

// Single source of truth for "how many tools" across the landing page — derived
// from the categories above so the headline + stats strip stay in sync whenever
// a tool is added or removed (no more hand-editing "15" / "Fifteen").
export const LANDING_TOOL_COUNT = LANDING_CATEGORIES.reduce((n, c) => n + c.keys.length, 0);


// Tools that carry the "New" badge on the landing grid. Kept in step with
// NEW_FEATURE_TOOLS and the hub badges by src/test/toolReleases.test.ts.
export const NEW_SERVICE_KEYS = new Set<LandingToolKey>([
  "inklab",
  "modkitviewer",
  "blinklab3d",
  "codevisualizer",
  "scriptdocs",
  "chevronbuilder",
  "creatorconvert",
  "handlingdiff",
  "resmon",
  "embroiderytool",
]);

/** Tools named explicitly in the site meta description; the rest are counted. */
export const LANDING_META_NAMED_TOOLS = ["Paint Booth", "UV Studio", "EUP Viewer"] as const;

/** The shared meta description used by app/layout.tsx and the landing page. */
export function landingMetaDescription(): string {
  const rest = LANDING_TOOL_COUNT - LANDING_META_NAMED_TOOLS.length;
  return `Make FiveM liveries, inspect EUP, build vehicle metas, pack resources — all in your browser. ${LANDING_META_NAMED_TOOLS.join(", ")} + ${rest} tools in one subscription.`;
}
